import { useEffect, useMemo, useState } from 'react'
import { BOM_IMPORT_REQUIRED, BOM_IMPORT_TARGETS, type BomImportTarget } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { fetchImportMappings, fetchUnits, importBom, type BomImportResult, type ImportMapping } from '../api/project-bom.api'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')

const ZUSTAND: Record<string, { text: string; klasse: string }> = {
  MATCHED: { text: 'zugeordnet', klasse: 'text-green-700 dark:text-green-400' },
  FREE: { text: 'frei', klasse: 'text-foreground' },
  DUPLICATE: { text: 'doppelt', klasse: 'text-amber-700 dark:text-amber-400' },
  INVALID: { text: 'ungültig', klasse: 'text-destructive' },
}

/**
 * AP-35 Detailblatt 5 6. — **eine Schaltfläche „Import"**, die Quelle im Dialog.
 *
 * Drei Schritte in einem Fenster: Datei und Zuordnung → Vorschau mit Ergebnis je
 * Zeile (Häkchen, Dubletten sind aus) → übernehmen. Die Vorschau ist dieselbe
 * Anfrage mit `dryRun` — gerechnet wird nur auf dem Server.
 */
export function BomImportDialog({
  projectId,
  positionId,
  onClose,
  onDone,
}: {
  projectId: string
  positionId: string
  onClose: () => void
  onDone: (text: string) => Promise<void>
}) {
  const [quelle, setQuelle] = useState<'CAD' | 'TABLE'>('CAD')
  const [datei, setDatei] = useState<File | null>(null)
  const [zuordnungen, setZuordnungen] = useState<ImportMapping[]>([])
  const [zuordnungId, setZuordnungId] = useState('')
  const [spalten, setSpalten] = useState<Partial<Record<BomImportTarget, string>>>({})
  const [einheiten, setEinheiten] = useState<Record<string, string>>({})
  const [mitKopf, setMitKopf] = useState(true)
  const [modus, setModus] = useState<'APPEND' | 'REPLACE'>('APPEND')
  const [vorschau, setVorschau] = useState<BomImportResult | null>(null)
  const [haken, setHaken] = useState<Set<number>>(new Set())
  const [speichernAls, setSpeichernAls] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    fetchImportMappings().then(setZuordnungen).catch(() => undefined)
    fetchUnits().then((u) => setCodes(u.map((x) => x.code))).catch(() => undefined)
  }, [])

  const passende = zuordnungen.filter((z) => z.kind === quelle)

  async function pruefen(ueberschreiben?: { columns?: typeof spalten; unitMap?: typeof einheiten }) {
    if (!datei) return
    setLaeuft(true)
    setFehler(null)
    try {
      const r = await importBom(projectId, positionId, datei, {
        source: quelle,
        mappingId: zuordnungId || undefined,
        columns: Object.keys(ueberschreiben?.columns ?? spalten).length ? (ueberschreiben?.columns ?? spalten) : undefined,
        unitMap: Object.keys(ueberschreiben?.unitMap ?? einheiten).length ? (ueberschreiben?.unitMap ?? einheiten) : undefined,
        hasHeader: mitKopf,
        mode: modus,
        dryRun: true,
      })
      setVorschau(r)
      setSpalten(r.mapping.columns)
      setEinheiten(r.mapping.unitMap ?? {})
      setHaken(new Set(r.rows.filter((z) => z.state === 'MATCHED' || z.state === 'FREE').map((z) => z.row)))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht lesen.'))
    } finally {
      setLaeuft(false)
    }
  }

  async function uebernehmen() {
    if (!datei || !vorschau) return
    setLaeuft(true)
    setFehler(null)
    try {
      const r = await importBom(projectId, positionId, datei, {
        source: quelle,
        columns: spalten,
        unitMap: einheiten,
        hasHeader: mitKopf,
        mode: modus,
        rows: [...haken],
        saveMappingAs: speichernAls.trim() || undefined,
      })
      await onDone(`${r.toImport ?? 0} Zeilen importiert${r.replaced ? `, ${r.replaced} ersetzt` : ''}${r.notReplaced?.length ? `, ${r.notReplaced.length} bestellte blieben` : ''}.`)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Import hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  // Einheiten der Datei, die es beim Mandanten so nicht gibt (Blatt 5 6. Einheiten-Übersetzung).
  const fremdeEinheiten = useMemo(() => {
    const bekannt = new Set(codes.map((c) => c.toLowerCase()))
    return [...new Set((vorschau?.rows ?? []).map((z) => z.unitCode).filter((u): u is string => !!u && !bekannt.has(u.toLowerCase())))]
  }, [vorschau, codes])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col space-y-3 overflow-hidden rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <h2 className="text-lg font-semibold">Import in die Stückliste</h2>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex overflow-hidden rounded-md border border-border text-sm" role="group" aria-label="Quelle">
            {(['CAD', 'TABLE'] as const).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setQuelle(q)
                  setZuordnungId('')
                  setVorschau(null)
                }}
                className={`px-3 py-1.5 ${quelle === q ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                {q === 'CAD' ? 'CAD-Stückliste' : 'Tabelle'}
              </button>
            ))}
          </div>
          <label className="text-xs text-muted-foreground">
            Datei (CSV oder XLSX)
            <input
              type="file"
              accept=".csv,.txt,.xlsx"
              className="mt-1 block text-sm"
              onChange={(e) => {
                setDatei(e.target.files?.[0] ?? null)
                setVorschau(null)
              }}
            />
          </label>
          <label className="text-xs text-muted-foreground">
            gespeicherte Zuordnung
            <select
              className={`${schmal} block w-56`}
              value={zuordnungId}
              onChange={(e) => {
                setZuordnungId(e.target.value)
                const m = zuordnungen.find((z) => z.id === e.target.value)
                if (m) {
                  setSpalten(m.columns)
                  setEinheiten(m.unitMap ?? {})
                  setMitKopf(m.hasHeader)
                }
              }}
            >
              <option value="">— neu zuordnen —</option>
              {passende.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={mitKopf} onChange={(e) => setMitKopf(e.target.checked)} /> erste Zeile ist Kopfzeile
          </label>
          <button type="button" disabled={!datei || laeuft} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50" onClick={() => void pruefen()}>
            Datei prüfen
          </button>
        </div>

        {vorschau && (
          <div className="min-h-0 flex-1 space-y-3 overflow-auto">
            <div className="grid gap-2 sm:grid-cols-4">
              {(Object.keys(BOM_IMPORT_TARGETS) as BomImportTarget[]).map((ziel) => (
                <label key={ziel} className={`block text-xs ${vorschau.missing.includes(ziel) ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {BOM_IMPORT_TARGETS[ziel]}
                  {BOM_IMPORT_REQUIRED.includes(ziel) && ' *'}
                  <select className={inlineInput} value={spalten[ziel] ?? ''} onChange={(e) => setSpalten((s) => ({ ...s, [ziel]: e.target.value || undefined }))}>
                    <option value="">—</option>
                    {vorschau.headers.map((h, i) => (
                      <option key={`${h}-${i}`} value={mitKopf ? h : String(i + 1)}>
                        {mitKopf ? h : `Spalte ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            {fremdeEinheiten.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Einheiten der Datei:</span>
                {fremdeEinheiten.map((u) => (
                  <label key={u} className="flex items-center gap-1">
                    „{u}" →
                    <select className={`${schmal} w-24`} value={einheiten[u] ?? ''} onChange={(e) => setEinheiten((m) => ({ ...m, [u]: e.target.value }))}>
                      <option value="">—</option>
                      {codes.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <button type="button" disabled={laeuft} className="rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => void pruefen({ columns: spalten, unitMap: einheiten })}>
                neu prüfen
              </button>
              <span>
                {vorschau.counts.matched} zugeordnet · {vorschau.counts.free} frei · {vorschau.counts.duplicate} doppelt · {vorschau.counts.invalid} ungültig
                {vorschau.weight ? ` · ${vorschau.weight.toLocaleString(navigator.language)} kg` : ''}
              </span>
              <select className={`${schmal} w-44`} value={modus} onChange={(e) => setModus(e.target.value as 'APPEND' | 'REPLACE')} aria-label="Modus">
                <option value="APPEND">anhängen</option>
                <option value="REPLACE">frühere Importe dieser Datei ersetzen</option>
              </select>
              {!!vorschau.notReplaced?.length && <span className="text-amber-700 dark:text-amber-400">{vorschau.notReplaced.length} bestellte Zeilen bleiben</span>}
            </div>

            {vorschau.missing.length === 0 && (
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="w-6" />
                    <th className="px-1 text-left">Zeile</th>
                    <th className="px-1 text-left">Bezeichnung</th>
                    <th className="px-1 text-right">Menge</th>
                    <th className="px-1 text-left">Einheit</th>
                    <th className="px-1 text-left">Werkstoff</th>
                    <th className="px-1 text-right">kg/E</th>
                    <th className="px-1 text-left">Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  {vorschau.rows.map((z) => (
                    <tr key={z.row} className="border-t border-border">
                      <td>
                        <input
                          type="checkbox"
                          disabled={z.state === 'INVALID'}
                          checked={haken.has(z.row)}
                          onChange={() =>
                            setHaken((h) => {
                              const n = new Set(h)
                              if (n.has(z.row)) n.delete(z.row)
                              else n.add(z.row)
                              return n
                            })
                          }
                          aria-label={`Zeile ${z.row}`}
                        />
                      </td>
                      <td className="px-1">{z.row}</td>
                      <td className="px-1">{z.name || '—'}</td>
                      <td className="px-1 text-right tabular-nums">{z.quantity ?? '—'}</td>
                      <td className="px-1">{z.unitCode ?? ''}</td>
                      <td className="px-1">{z.material ?? ''}</td>
                      <td className="px-1 text-right tabular-nums">{z.weightPerUnit ?? ''}</td>
                      <td className={`px-1 ${ZUSTAND[z.state]?.klasse ?? ''}`}>{ZUSTAND[z.state]?.text ?? z.state}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {vorschau && vorschau.missing.length === 0 && (
            <input className={`${schmal} mr-auto w-64`} placeholder="Zuordnung speichern als … (optional)" value={speichernAls} onChange={(e) => setSpeichernAls(e.target.value)} />
          )}
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!vorschau || vorschau.missing.length > 0 || haken.size === 0 || laeuft}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() => void uebernehmen()}
          >
            {haken.size} Zeilen übernehmen
          </button>
        </div>
      </div>
    </div>
  )
}
